import Button from "@/common/components/Button";
import { BillingEventData } from "@/common/types/billing/event.type";
import { formatCurrency, formatDateTime } from "@/common/utils/formatters";
import { transformToNumber } from "@/common/utils/transformers";
import { eventableTypeTranslations } from "@/features/billingEvents/constants/eventableType.constant";
import { DownloadIcon, PiggyBankIcon } from "lucide-react";
import React from "react";
import { twMerge } from "tailwind-merge";
import ConsumptionDetails from "../components/ConsumptionDetails";
import FeesDetails from "../components/FeesDetails";
import RefundDetails from "../components/RefundDetails";
import PrintReceiptButton from "@/common/components/PrintReceiptButton";

export class BillingEventTransformer {
  static tableTransformer({
    fetchBillingEvent,
    disableActions,
  }: {
    fetchBillingEvent?: (id: string) => void
    disableActions?: boolean
  }) {
    return (events: BillingEventData[]) => {
      return events.map((event) => {
        const events = event.eventable
        const data = events?.refundable ?? events?.related_eventable?.refundable ?? events?.related_eventable ?? events

        // WARN: We are here changing the logic to priorize the original price over the real total amount,
        // actually, when the original price/total is present, we are hiding the actual price/total and subtracting of the fee,
        // the difference between the two numbers. This is being made for companies that has dynamic price and dont want to know that this
        // price that is being applied as a fee
        const hasOriginalTotalAmount = data?.original_total_amount !== null && data?.original_total_amount !== undefined
        const fixedPriceDiscount = hasOriginalTotalAmount
          ? transformToNumber({ value: data?.original_total_amount }) - transformToNumber({ value: data?.total_amount })
          : 0
        const adjustedFeeTotal = transformToNumber({ value: event.fee_total }) - fixedPriceDiscount
        const displayedTotalAmount = hasOriginalTotalAmount
          ? transformToNumber({ value: data?.original_total_amount })
          : transformToNumber({ value: event.total_amount })

        return {
          protocol_number: event.protocol_number,
          bill: '',
          eventable_type: eventableTypeTranslations[event.eventable_type as keyof typeof eventableTypeTranslations] || 'Nao implementado',
          details: eventableDetailsComponent[event.eventable_type] ? React.createElement(eventableDetailsComponent[event.eventable_type], { data: event.eventable }) : '',
          gross_value: event.total_amount
            ? formatCurrency(displayedTotalAmount - adjustedFeeTotal)
            : "",
          total_amount: event.total_amount
            ? formatCurrency(displayedTotalAmount)
            : "--",
          fee_total_amount: <FeesDetails {...event} fee_total={String(adjustedFeeTotal)} />,
          income_tax_total: event.income_tax_total ? formatCurrency(parseFloat(event.income_tax_total)) : '',
          card: data?.card?.last_four_digits.padStart(8, '*') || '',
          equipment_field_for_display_value: data?.card?.equipment_field_for_display_value || '',
          buyer: data?.buyer?.full_name || '',
          capture_method: event?.eventable?.capture_method?.display_name || '--',
          created_at: formatDateTime(new Date(event?.created_at || 0)),
          client_branch: event.eventable?.client_branch?.display_name,
          actions: (
            <div className="flex items-center gap-2">
              <PrintReceiptButton eventId={event.protocol_number} showConfirmationModal />

              {fetchBillingEvent && (
                <Button
                  className="bg-transparent hover:bg-transparent disabled:hover:bg-transparent p-0 mx-auto"
                  onClick={() => fetchBillingEvent(event.id)}
                  disabled={disableActions}
                  operationKlassToCheckPermission="Establishment::Billing::Events::Show"
                >
                  <DownloadIcon strokeWidth={1} />
                </Button >
              )}
            </div>
          ),
        }
      })
    };
  }

  static feesDetailsTableTransformer() {
    return (fees: BillingEventData['fees']) => {
      return fees?.map((fee) => {
        const totalFee = Number(fee.amount)
        return ({
          amount: (
            <p className={twMerge(totalFee > 0 ? 'text-success' : totalFee < 0 ? 'text-danger-soft' : '')}>
              {formatCurrency(totalFee)}
            </p>
          ),
          display_name: fee?.fee_source?.fee_template?.display_name ? (
            <div className="flex gap-2 items-center">
              <PiggyBankIcon strokeWidth={0.6} className="w-5 h-5" />

              <div>
                <p>{fee.fee_source.fee_template.display_name}</p>
              </div>
            </div>
          ) : '',
          product_display_name: fee?.source?.display_name ? (
            <div className="flex gap-2 items-center">
              <img src="/Paper.svg" />

              <div>
                <p>{fee.source.display_name}</p>
              </div>
            </div>
          ) : '',
        });
      }) || [];
    };
  }

  static quickSearchModelTransformer() {
    return (events: BillingEventData[]) => {
      return events.map((event) => ({
        route: `/billing-events?search=${event.protocol_number}`,
        items: [
          ['Transação', event.protocol_number],
          ['Tipo', eventableTypeTranslations[event.eventable_type as keyof typeof eventableTypeTranslations] || ''],
          ['Detalhes', eventableDetailsQuickSearchValue[event.eventable_type] ? eventableDetailsQuickSearchValue[event.eventable_type](event) : ''],
        ],
      }));
    };
  }
}

const eventableDetailsComponent: { [key: string]: React.ComponentType<any>; } = {
  'Client::Consumption': ConsumptionDetails,
  'Billing::Refund': RefundDetails,
  'Billing::TefTransaction': ConsumptionDetails,
};

export const eventableDetailsQuickSearchValue: { [key: string]: (event: BillingEventData) => string; } = {
  'Client::Consumption': (event) => `Empresa cliente: ${event?.eventable?.client_branch?.general_information?.fantasy_name || '--'}`,
  'Billing::Refund': (event) => `N. Protocolo: ${event?.eventable?.refundable?.protocol_number || '--'}`,
  'Billing::TefTransaction': (event) => `Empresa cliente: ${event?.eventable?.client_branch?.general_information?.fantasy_name}`,
};
